/**
 * Site KPI — Quality sub-tab: PPM / NCRs / Defect Rate cards and the six
 * quality charts.
 *
 * CR-5868: split out of SiteKPITab.tsx (500-line limit). Markup unchanged
 * except the supplier-portal "BY SITE" chart title: the axis is the CUSTOMER
 * site (company_kpi groups by po.customer), so a supplier sees it named so.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import {
  BarChart, Bar, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts'
import type { DashboardData } from '../types'
import { useFormatters, usePreferences } from '../../../contexts/PreferencesContext'
import { useAxisAmount } from '../charts/useAxisAmount'
import { ChartWrapper } from '../ChartWrapper'
import { CircularProgress, QualityMetricComparisonCard } from '../SiteKPIComponents'
import { NO_JUDGEMENT_COLOR } from '../kpiTargets'
import { useQualityMeasureLabel } from '../../../lib/qualityLabels'
import { formatKpiPercent } from '../../../lib/kpiPercent'
import type { SiteKPIMetrics } from './siteKPIMetrics'
import type { SiteKPIDrilldowns } from './useSiteKPIDrilldowns'
import { liveNcrsBarClick } from '../liveNcrsDrill'

interface SiteKPIQualityPanelProps {
  data: DashboardData
  metrics: SiteKPIMetrics
  drill: SiteKPIDrilldowns
  showOTIF: boolean
  /** CR-5868: supplier portal — the "site" axis is the customer site. */
  isSupplier: boolean
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#ef4444',
  major: '#f59e0b',
  minor: '#3b82f6',
  low: '#10b981',
}

export function SiteKPIQualityPanel({ data, metrics, drill, showOTIF, isSupplier }: SiteKPIQualityPanelProps) {
  const { formatCurrency } = useFormatters()
  const axisAmount = useAxisAmount() // CR-7120: numbers only on graph axes
  // OTIF/KPI percentage decimals from Admin Setup -> Regional -> Decimal
  // Precision. 0 keeps whole-number ring labels; 1-2 shows e.g. 8.6%.
  const { preferences } = usePreferences()
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0
  const qualityLabel = useQualityMeasureLabel()
  const { handleChartClick } = drill
  const { siteKPIData, ppmTarget, ppmGradeBands, ppmComparisonValues } = metrics
  const ppmNow = typeof data.quality?.ppm === 'number' ? data.quality.ppm : null

  const ppmTrend = data.charts?.ppmTrend || []
  const severityData = Object.entries(data.quality?.ncrsBySeverity || {}).map(([key, value]) => ({
    severity: key.charAt(0).toUpperCase() + key.slice(1),
    count: value,
  }))
  const rejectValueData = data.charts?.ppmNCRs?.slice(0, 6).map((ncr) => ({
    month: ncr.date_raised?.substring(0, 7) || 'Unknown',
    value: ncr.reject_value || 0,
  })) || []

  return (
    <div className="space-y-6">
      {/* KPI Metrics Row - PPM, NCRs, Defect Rate */}
      <div className={`grid gap-4 ${showOTIF ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {/* PPM Card - Side-by-side comparison of all periods */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{qualityLabel}</CardTitle>
          </CardHeader>
          <CardContent>
            <QualityMetricComparisonCard
              values={ppmComparisonValues}
              target={ppmTarget}
              bands={ppmGradeBands}
            />
          </CardContent>
        </Card>

        {/* NCRs Card */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">NCRs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <div className="text-xs text-muted-foreground">Open</div>
                <div className="text-lg font-bold text-amber-600">
                  {data.quality?.ncrsByStatus?.['open'] || 0}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Closed</div>
                <div className="text-lg font-bold text-green-600">
                  {data.quality?.ncrsByStatus?.['closed'] || 0}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Overdue</div>
                <div className="text-lg font-bold text-red-600">
                  {data.quality?.ncrsByStatus?.['overdue'] || 0}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total</div>
                <div className="text-lg font-bold text-blue-600">
                  {data.quality?.ncrs || 0}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Defect Rate Card - derived from PPM */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">DEFECT RATE</CardTitle>
          </CardHeader>
          <CardContent>
            {/* CR-7714 FE-20: no PPM is N/A, not a green 0%; no target is
                grey, not judged against an invented one. */}
            {ppmNow === null ? (
              <div className="flex items-center justify-center py-8 text-2xl font-bold text-muted-foreground">N/A</div>
            ) : (
              <>
                <div className="flex items-center justify-center py-2">
                  <CircularProgress
                    value={Math.min(ppmNow / 10000, 100)}
                    size={100}
                    strokeWidth={10}
                    color={ppmTarget === null ? NO_JUDGEMENT_COLOR : ppmNow > ppmTarget ? '#ef4444' : '#10b981'}
                    decimals={kpiDecimals}
                  />
                </div>
                <p className="text-xs text-center text-muted-foreground mt-1">
                  {formatKpiPercent(ppmNow / 10000, kpiDecimals)}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 - PPM Trend and NCR Breakdown */}
      <div className="grid grid-cols-2 gap-4">
        <ChartWrapper
          title={`${qualityLabel} TREND`}
          data={ppmTrend}
          exportFilename="ppm-trend"
          legend={[
            { color: '#ef4444', label: qualityLabel },
            { color: '#3b82f6', label: 'Target' },
          ]}
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={ppmTrend}
              onClick={(chartData) => handleChartClick(chartData, 'ppm')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              {/* CR-4537: interval={0} forces every month tick to render on long ranges; angle + smaller font keep them readable */}
              <XAxis dataKey="month" tick={{ fontSize: ppmTrend.length > 24 ? 9 : 11 }} interval={0} angle={ppmTrend.length > 12 ? -45 : 0} textAnchor={ppmTrend.length > 12 ? 'end' : 'middle'} height={ppmTrend.length > 12 ? 55 : 30} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="ppm" fill="#ef4444" name={qualityLabel} radius={[4, 4, 0, 0]}>
                {ppmTrend.map((entry, index: number) => (
                  <Cell
                    key={`cell-${index}`}
                    // CR-7714 FE-20: no PPM or no target is grey, never judged against null (0).
                    fill={entry.ppm == null || entry.target == null ? NO_JUDGEMENT_COLOR : entry.ppm <= entry.target ? '#10b981' : '#ef4444'}
                  />
                ))}
              </Bar>
              <Line type="monotone" dataKey="target" stroke="#3b82f6" strokeWidth={2} dot={false} />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <ChartWrapper
          title="NCR BREAKDOWN < 12 MONTHS"
          data={siteKPIData.ncrBreakdown}
          exportFilename="ncr-breakdown"
          legend={[
            { color: '#fbbf24', label: 'Open' },
            { color: '#10b981', label: 'Closed' },
            { color: '#ef4444', label: 'Overdue' },
          ]}
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={siteKPIData.ncrBreakdown}
              onClick={(chartData) => handleChartClick(chartData, 'ncrs')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="open" stackId="a" fill="#fbbf24" name="Open" />
              <Bar dataKey="closed" stackId="a" fill="#10b981" name="Closed" />
              <Bar dataKey="overdue" stackId="a" fill="#ef4444" name="Overdue" />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      {/* Charts Row 2 - PPM by Site and NCR by Severity */}
      <div className="grid grid-cols-2 gap-4">
        <ChartWrapper
          // CR-5868: the bars are customer sites (company_kpi.ppm_by_company);
          // on the supplier portal name them so.
          title={`${qualityLabel} BY ${isSupplier ? 'CUSTOMER SITE' : 'SITE'}`}
          data={siteKPIData.ppmBySite}
          exportFilename="ppm-by-site"
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={siteKPIData.ppmBySite}
              onClick={(chartData) => handleChartClick(chartData, 'ppm')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              {/* CR-5868: rows are keyed by `company` (company_kpi.ppm_by_company
                  carries no `site` alias, unlike delivery_by_company) — the old
                  `site` dataKey never matched, so the axis had no categories and
                  the chart drew empty while the PPM card showed a value. Same
                  defect CR-6286 fixed on Depth of Delivery. */}
              <XAxis dataKey="company" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="ppm" fill="#ef4444" name={qualityLabel} radius={[4, 4, 0, 0]}>
                {(siteKPIData.ppmBySite || []).map((entry, index: number) => (
                  <Cell
                    key={`cell-${index}`}
                    // CR-7714 FE-20: judged against the page's PPM target (the
                    // rows carry none; the old `|| 500` invented one). No PPM or
                    // no target: grey. Per-site targets in a group view: CR-8728.
                    fill={entry.ppm == null || ppmTarget === null ? NO_JUDGEMENT_COLOR : entry.ppm <= ppmTarget ? '#10b981' : '#ef4444'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <ChartWrapper
          title="NCRs BY SEVERITY"
          data={severityData}
          exportFilename="ncrs-by-severity"
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={severityData}
              layout="vertical"
              onClick={(chartData) => handleChartClick(chartData, 'ncrs')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="severity" tick={{ fontSize: 11 }} width={80} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="count" name="NCRs" radius={[0, 4, 4, 0]}>
                {Object.keys(data.quality?.ncrsBySeverity || {}).map((key, index) => (
                  <Cell key={`cell-${index}`} fill={SEVERITY_COLORS[key] || '#6b7280'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      {/* Charts Row 3 - Live NCRs and Reject Value */}
      <div className="grid grid-cols-2 gap-4">
        <ChartWrapper
          title="LIVE NCRs OVERDUE"
          data={data.charts?.liveNCRsOverdue || []}
          exportFilename="live-ncrs-overdue"
          legend={[
            { color: '#ef4444', label: 'Overdue' },
            { color: '#f59e0b', label: 'Pending' },
          ]}
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={data.charts?.liveNCRsOverdue || []}
              onClick={(chartData) => handleChartClick(chartData, 'liveNcrs')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="overdue" stackId="a" fill="#ef4444" name="Overdue" onClick={liveNcrsBarClick('overdue', (m, seg) => handleChartClick({ activeLabel: m }, 'liveNcrs', seg))} />
              <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending" onClick={liveNcrsBarClick('pending', (m, seg) => handleChartClick({ activeLabel: m }, 'liveNcrs', seg))} />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <ChartWrapper
          title="REJECT VALUE BY MONTH"
          data={rejectValueData}
          exportFilename="reject-value-by-month"
        >
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart
              data={rejectValueData}
              onClick={(chartData) => handleChartClick(chartData, 'ncrs')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={axisAmount}
              />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => formatCurrency(value)} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#ef4444"
                fill="#fecaca"
                name="Reject Value"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>
    </div>
  )
}
